import { useEffect, useState, useCallback } from "react";
import { Loader2, Trash2, User, Package, Phone, MapPin, ChevronDown, Gift, CheckCircle2 } from "lucide-react";
import { api, formatPrice } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { toast } from "sonner";

const STATUSES = ["nuevo", "confirmado", "enviado", "entregado", "cancelado"];
const statusColor = {
  nuevo: "bg-teal/30 text-charcoal",
  confirmado: "bg-peach text-coral-dark",
  enviado: "bg-sage/60 text-charcoal",
  entregado: "bg-coral text-white",
  cancelado: "bg-charcoal/10 text-muted-moss",
};

export const OrdersManager = () => {
  const { settings } = useSettings();
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("orders");
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, c, m] = await Promise.all([api.get("/admin/orders"), api.get("/admin/customers"), api.get("/admin/members")]);
      setOrders(o.data);
      setCustomers(c.data);
      setMembers(m.data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    await api.patch(`/admin/orders/${id}`, { status });
    setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, status } : o)));
    toast.success("Estado actualizado");
  };

  const remove = async (o) => {
    if (!window.confirm("¿Eliminar este pedido?")) return;
    await api.delete(`/admin/orders/${o.id}`); toast.success("Eliminado"); load();
  };

  const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("es-CR", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <button data-testid="orders-view-orders" onClick={() => setView("orders")} className={`px-4 py-2 rounded-2xl text-sm font-semibold border ${view === "orders" ? "bg-coral text-white border-coral" : "bg-white text-charcoal border-[#E3E7E2]"}`}>Pedidos ({orders.length})</button>
        <button data-testid="orders-view-customers" onClick={() => setView("customers")} className={`px-4 py-2 rounded-2xl text-sm font-semibold border ${view === "customers" ? "bg-coral text-white border-coral" : "bg-white text-charcoal border-[#E3E7E2]"}`}>Clientes ({customers.length})</button>
        <button data-testid="orders-view-members" onClick={() => setView("members")} className={`px-4 py-2 rounded-2xl text-sm font-semibold border ${view === "members" ? "bg-coral text-white border-coral" : "bg-white text-charcoal border-[#E3E7E2]"}`}>Miembros 5% ({members.length})</button>
      </div>

      {loading ? (
        <div className="grid place-items-center py-16"><Loader2 className="w-7 h-7 animate-spin text-coral" /></div>
      ) : view === "orders" ? (
        orders.length === 0 ? (
          <div className="text-center py-16 text-muted-moss"><Package className="w-12 h-12 mx-auto mb-3 opacity-40" /><p className="font-semibold">Aún no hay pedidos</p><p className="text-sm">Los pedidos enviados por WhatsApp aparecerán aquí.</p></div>
        ) : (
          <div className="space-y-3">
            {orders.map((o) => (
              <div key={o.id} data-testid={`admin-order-${o.id}`} className="bg-white rounded-2xl border border-[#E3E7E2] overflow-hidden">
                <div className="p-4 flex flex-wrap items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-[11px] font-bold uppercase px-2 py-0.5 rounded-full ${statusColor[o.status] || ""}`}>{o.status}</span>
                      <span className="text-xs text-muted-moss">{fmtDate(o.created_at)}</span>
                    </div>
                    <p className="font-semibold text-charcoal mt-1">{o.customer_name}</p>
                    <p className="text-xs text-muted-moss flex items-center gap-1"><Phone className="w-3 h-3" /> {o.phone}</p>
                  </div>
                  <span className="font-display text-lg font-bold text-coral">{formatPrice(o.total, settings.currency)}</span>
                  <select value={o.status} onChange={(e) => setStatus(o.id, e.target.value)} className="inp w-36">
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <button onClick={() => setExpanded(expanded === o.id ? null : o.id)} className="grid place-items-center w-9 h-9 rounded-xl bg-cream hover:bg-sage/40"><ChevronDown className={`w-4 h-4 transition-transform ${expanded === o.id ? "rotate-180" : ""}`} /></button>
                  <button onClick={() => remove(o)} className="grid place-items-center w-9 h-9 rounded-xl bg-cream text-muted-moss hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                </div>
                {expanded === o.id && (
                  <div className="px-4 pb-4 border-t border-[#E3E7E2] pt-3 space-y-2">
                    <p className="text-sm text-charcoal flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-teal shrink-0" /> {o.address || "Sin dirección"}</p>
                    {o.notes && <p className="text-sm text-muted-moss">📝 {o.notes}</p>}
                    <div className="rounded-xl bg-cream-2 p-3 space-y-1">
                      {o.items.map((it, i) => (
                        <div key={i} className="flex justify-between text-sm">
                          <span className="text-charcoal">{it.qty} × {it.name}{it.variant ? ` (${it.variant})` : ""} {it.sku ? <span className="text-muted-moss">· {it.sku}</span> : null}</span>
                          <span className="font-semibold text-charcoal">{formatPrice(it.unit_price * it.qty, settings.currency)}</span>
                        </div>
                      ))}
                    </div>
                    <a href={`https://wa.me/${o.phone.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-semibold text-coral">Contactar cliente por WhatsApp</a>
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      ) : view === "customers" ? (
        customers.length === 0 ? (
          <div className="text-center py-16 text-muted-moss"><User className="w-12 h-12 mx-auto mb-3 opacity-40" /><p className="font-semibold">Aún no hay clientes</p></div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {customers.map((c, i) => (
              <div key={i} data-testid={`admin-customer-${i}`} className="bg-white rounded-2xl border border-[#E3E7E2] p-4">
                <div className="flex items-center gap-3">
                  <span className="grid place-items-center w-11 h-11 rounded-full bg-peach text-coral font-bold font-display">{(c.name || "?").charAt(0)}</span>
                  <div className="min-w-0">
                    <p className="font-semibold text-charcoal truncate">{c.name}</p>
                    <p className="text-xs text-muted-moss">{c.phone}</p>
                  </div>
                </div>
                <div className="flex justify-between mt-3 text-sm">
                  <span className="text-muted-moss">{c.orders} pedido(s)</span>
                  <span className="font-bold text-coral">{formatPrice(c.total_spent, settings.currency)}</span>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        members.length === 0 ? (
          <div className="text-center py-16 text-muted-moss"><Gift className="w-12 h-12 mx-auto mb-3 opacity-40" /><p className="font-semibold">Aún no hay miembros registrados</p><p className="text-sm">Quienes se registren para el 5% aparecerán aquí.</p></div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {members.map((m) => (
              <div key={m.id} data-testid={`admin-member-${m.id}`} className="bg-white rounded-2xl border border-[#E3E7E2] p-4 flex items-center gap-3">
                <span className={`grid place-items-center w-11 h-11 rounded-full font-bold font-display ${m.discount_used ? "bg-charcoal/10 text-muted-moss" : "bg-peach text-coral"}`}>
                  {m.discount_used ? <CheckCircle2 className="w-5 h-5" /> : <Gift className="w-5 h-5" />}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-charcoal truncate">{m.name}</p>
                  <p className="text-xs text-muted-moss">{m.phone}{m.email ? ` · ${m.email}` : ""}</p>
                </div>
                <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${m.discount_used ? "bg-charcoal/10 text-muted-moss" : "bg-coral text-white"}`}>
                  {m.discount_used ? "5% usado" : "5% disponible"}
                </span>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
};
